import { type AddressLite, formatAddressSlug } from '@mollusk/shared';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { api } from '../../api/endpoints';
import { QueryErrorAlert } from '../../components/QueryErrorAlert';

export type DrilldownScope =
  | { scope: 'street'; street: string; city: string; state: string; postal_code: string }
  | { scope: 'city'; city: string; state: string }
  | { scope: 'zip'; postal_code: string };

// "City, ST ZIP" — state and zip separated by a space (not a comma), city by a
// comma. Empty parts are dropped.
function formatCityStateZip(
  city?: string | null,
  state?: string | null,
  postal_code?: string | null,
): string {
  const stateZip = [state, postal_code].filter(Boolean).join(' ');
  return [city, stateZip].filter(Boolean).join(', ');
}

export function AddressDrilldownModal({
  open,
  scope,
  onClose,
}: {
  open: boolean;
  scope: DrilldownScope | null;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const q = useQuery({
    queryKey: ['addresses', scope],
    enabled: open && !!scope,
    queryFn: () => api.addresses(scope as DrilldownScope),
  });

  const onPick = (a: AddressLite) => {
    const full = [a.address_line1, formatCityStateZip(a.city, a.state, a.postal_code)]
      .filter(Boolean)
      .join(', ');
    navigate({ to: '/address/$slug', params: { slug: formatAddressSlug(full) } });
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        Addresses
        {q.data ? (
          <Typography variant="body2" color="text.secondary">
            showing first {q.data.addresses.length} of {q.data.total}
          </Typography>
        ) : null}
      </DialogTitle>
      <DialogContent dividers sx={{ height: 480 }}>
        {q.isLoading ? <Typography>Loading…</Typography> : null}
        {q.error && !q.data ? (
          <QueryErrorAlert
            error={q.error}
            failed="Addresses could not be loaded. Try again in a moment."
            tooExpensive="This list would scan more data than allowed, so it can't be shown."
          />
        ) : null}
        {q.data ? (
          <List dense disablePadding>
            {q.data.addresses.map((a) => (
              <ListItemButton key={a.address_line1} onClick={() => onPick(a)}>
                <ListItemText
                  primary={a.address_line1}
                  secondary={formatCityStateZip(a.city, a.state, a.postal_code)}
                />
              </ListItemButton>
            ))}
          </List>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
